import { useFormikContext } from 'formik';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type FinanceProductFormValues } from '../..';
import { type BalloonSettingsPayload, type BalloonGfvSettingsPayload } from '../../../../../../api';
import useCompanyFormats from '../../../../../../utilities/useCompanyFormats';
import useSelectedFinderVehicles from '../../../shared/useSelectedFinderVehicles';
import useSelectedVariants from '../../../shared/useSelectedVariants';
import { type BalloonTableRecord } from './shared';
import useColumns from './useColumns';
import useRecords from './useRecords';
import useTerms from './useTerms';
import useVehicleReferenceParameterRecords from './useVehicleReferenceParameterRecords';

const useBalloonReferenceData = (
    fieldName: string,
    balloonSettings: BalloonSettingsPayload | BalloonGfvSettingsPayload,
    isDisabled?: boolean
) => {
    const { t } = useTranslation(['financeProductDetails']);
    const { formatAmount } = useCompanyFormats();

    const { values, setFieldValue } = useFormikContext<FinanceProductFormValues>();

    const { variantSuiteIds, term: termSettings } = values;
    const terms = useTerms(termSettings);

    const variants = useSelectedVariants(variantSuiteIds);
    const finderVehicles = useSelectedFinderVehicles(variantSuiteIds);

    const variantRecords = useRecords(variants, balloonSettings, terms);
    const finderVehicleRecords = useRecords(finderVehicles, balloonSettings, terms);

    const vehicleReferenceParameterRecords = useVehicleReferenceParameterRecords({
        balloonSettings,
        formatAmount,
        vehicleReferenceParameters: values.vehicleReferenceParameters,
        terms,
    });

    const onUpdate = useCallback(
        (record: BalloonTableRecord) => {
            // clone current records
            const current = [
                ...variantRecords,
                ...finderVehicleRecords,
                ...vehicleReferenceParameterRecords.variant,
                ...vehicleReferenceParameterRecords.finderVehicle,
            ];

            // find target index
            const { variantSuiteId: targetId } = record;
            const index = current.findIndex(({ variantSuiteId }) => variantSuiteId === targetId);

            // update target
            current.splice(index, 1, record);
            const updated = current
                .flatMap(record =>
                    terms.map(term => ({
                        variantSuiteId: record.variantSuiteId,
                        term,
                        value: record[`term${term}`],
                    }))
                )
                .filter(({ value }) => Number.isFinite(value));

            // then update formik
            setFieldValue(`${fieldName}.table`, updated);
        },
        [
            fieldName,
            finderVehicleRecords,
            setFieldValue,
            terms,
            variantRecords,
            vehicleReferenceParameterRecords.finderVehicle,
            vehicleReferenceParameterRecords.variant,
        ]
    );

    const variantColumns = useColumns(
        t('financeProductDetails:tables.balloon.columnTitles.variantTerm'),
        terms,
        variants,
        isDisabled,
        onUpdate
    );

    const finderVehicleColumns = useColumns(
        t('financeProductDetails:tables.balloon.columnTitles.finderVehicleTerm'),
        terms,
        finderVehicles,
        isDisabled,
        onUpdate
    );

    return useMemo(
        () => ({
            variantColumns,
            variantRecords: [...variantRecords, ...vehicleReferenceParameterRecords.variant],
            finderVehicleColumns,
            finderVehicleRecords: [...finderVehicleRecords, ...vehicleReferenceParameterRecords.finderVehicle],
        }),
        [
            finderVehicleColumns,
            finderVehicleRecords,
            variantColumns,
            variantRecords,
            vehicleReferenceParameterRecords?.finderVehicle,
            vehicleReferenceParameterRecords?.variant,
        ]
    );
};

export default useBalloonReferenceData;
